import { useEffect, useRef } from 'react';
import { useAppContext } from '../store/AppContext';
import useCartService from '../services/cartService';
import useCampService from '../services/campService';
import updatePlayersInCart from '../utils/booking/updatePlayersInCart';

/**
 * Fetches the server-side cart (`GET /carts/mine/updated`) and applies the
 * latest server prices (and hosting updates) to the local AppContext.
 *
 * Used by both the desktop `Cart` and the mobile `MobileCart` so a user landing
 * directly on a mobile page also sees the price-change warnings, not only the
 * stock errors. Runs at most once per mount (guarded by refs).
 */
const useApplyServerCartPrices = () => {
  const [{ players, hosting }, stateDispatch] = useAppContext();
  const [, , , { checkCartPriceChange }] = useCartService();
  const [, , , { getCampsMappedByIds }] = useCampService();

  const didApplyRef = useRef(false);
  const isApplyingRef = useRef(false);

  useEffect(() => {
    const applyServerPricesIfNeeded = async () => {
      if (didApplyRef.current) return;
      if (isApplyingRef.current) return;
      if (!players || players.length === 0) return;

      isApplyingRef.current = true;
      try {
        const serverSideCart = await checkCartPriceChange();
        if (!serverSideCart || !serverSideCart.cart) {
          isApplyingRef.current = false;
          return;
        }

        const campsMappedById = await getCampsMappedByIds(players.map((p) => p.stage.campId));

        let updatedHosting = hosting;
        const updatedPlayers = players.map((player) => {
          const result = updatePlayersInCart(
            player,
            serverSideCart.cart,
            campsMappedById[player.stage.campId],
            updatedHosting,
            false,
          );
          if (result && result.player) {
            if (result.hosting) updatedHosting = result.hosting;
            return result.player;
          }
          return result;
        });

        stateDispatch({ type: 'SET_PLAYERS', payload: updatedPlayers });
        if (updatedHosting) stateDispatch({ type: 'SET_HOSTING', payload: updatedHosting });

        didApplyRef.current = true;
      } catch (e) {
        console.error('Error applying server prices:', e);
      } finally {
        isApplyingRef.current = false;
      }
    };

    applyServerPricesIfNeeded();
  }, [players]);
};

export default useApplyServerCartPrices;
